import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import {Form, Button, FieldTextInput, IconUpload, IconFileType, IconClose} from '../../components';
import * as validators from '../../util/validators';
import Reaptcha from 'reaptcha';

import css from './JobsPage.module.css';

const { REACT_APP_RECAPTCHA_SITE_KEY } = process.env;

const JobsFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        form,
        rootClassName,
        className,
        formId,
        handleSubmit,
        submitInProgress,
        intl,
        invalid,
        uploadedCoverLetter,
        uploadedResume,
        setCoverLetter,
        setUploadedResume,
        submitApplicationSuccess,
        submitApplicationError,
      } = fieldRenderProps;

      const [isVerified, setVerified] = useState(false);

      const onVerify = recaptchaResponse => {
        setVerified(true);
      };

      const handleCoverLetterEvent = e => setCoverLetter(...e.target.files);
      const handleResumeEvent = e => setUploadedResume(...e.target.files);

      const removeResumeEvent = () => setUploadedResume({});
      const removeCoverLetterEvent = () => setCoverLetter({});

      if (submitApplicationSuccess) form.restart();

      // name
      const nameLabel = intl.formatMessage({
        id: 'ContactForm.nameLabel',
      });
      const namePlaceholder = intl.formatMessage({
        id: 'ContactForm.namePlaceholder',
      });
      // email
      const emailLabel = intl.formatMessage({
        id: 'ContactForm.emailLabel',
      });
      const emailPlaceholder = intl.formatMessage({
        id: 'ContactForm.emailPlaceholder',
      });
      const emailRequiredMessage = intl.formatMessage({
        id: 'ContactForm.emailRequired',
      });
      const emailRequired = validators.required(emailRequiredMessage);
      const emailInvalidMessage = intl.formatMessage({
        id: 'ContactForm.emailInvalid',
      });
      const emailValid = validators.emailFormatValid(emailInvalidMessage);

      // message
      const messageLabel = intl.formatMessage({
        id: 'ContactForm.messageLabel',
      });
      const messagePlaceholder = intl.formatMessage({
        id: 'ContactForm.messagePlaceholder',
      });
      const messageRequiredMessage = intl.formatMessage({
        id: 'ContactForm.messageRequired',
      });
      const messageRequired = validators.required(messageRequiredMessage);

      const classes = classNames(rootClassName || css.root, className);
      const submitDisabled =
        invalid ||
        submitInProgress ||
        !uploadedCoverLetter?.name ||
        !uploadedResume?.name ||
        !isVerified;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div className={css.inputsWrapper}>
            <FieldTextInput
              className={css.contactUsInput}
              type="text"
              id={'name'}
              name="name"
              label={nameLabel}
              placeholder={namePlaceholder}
              validate={messageRequired}
            />
            <FieldTextInput
              className={css.contactUsInput}
              type="email"
              id={'email'}
              name="email"
              label={emailLabel}
              placeholder={emailPlaceholder}
              validate={validators.composeValidators(emailRequired, emailValid)}
            />
          </div>

          <div className={css.fileUpload}>
            <label htmlFor='coverLetterUpload' className={css.fileUploadBtn}>
              <input
                onChange={e => {
                  handleCoverLetterEvent(e);
                  e.target.value = null;
                }}
                id='coverLetterUpload'
                type='file'
                accept='application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              />
              <IconUpload />
              <FormattedMessage id='JobsForm.coverLetterLabel'/>
            </label>
          </div>
          <ul className={css.fileUploadList}>
            {uploadedCoverLetter?.name && (
              <li key='file_coverLetter' className={css.fileUploadItem}>
                <span className={css.fileUploadName}>
                  <span className={css.fileUploadIcon}>
                    <IconFileType fileType={uploadedCoverLetter.type || uploadedCoverLetter.mimeType}/>
                  </span>
                  {uploadedCoverLetter.fileName || uploadedCoverLetter.name}
                </span>
                <span onClick={() => removeCoverLetterEvent()} className={css.fileUploadRemove}>
                  <IconClose/>
                </span>
              </li>
            )}
          </ul>

          <div className={css.fileUpload}>
            <label htmlFor='resumeUpload' className={css.fileUploadBtn}>
              <input
                onChange={e => {
                  handleResumeEvent(e);
                  e.target.value = null;
                }}
                id='resumeUpload'
                type='file'
                accept='application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              />
              <IconUpload />
              <FormattedMessage id='JobsForm.resumeLabel'/>
            </label>
          </div>
          <ul className={css.fileUploadList}>
            {uploadedResume?.name && (
              <li key='file_resume' className={css.fileUploadItem}>
                <span className={css.fileUploadName}>
                  <span className={css.fileUploadIcon}>
                    <IconFileType fileType={uploadedResume.type || uploadedResume.mimeType}/>
                  </span>
                  {uploadedResume.fileName || uploadedResume.name}
                </span>
                <span onClick={() => removeResumeEvent()} className={css.fileUploadRemove}>
                  <IconClose/>
                </span>
              </li>
            )}
          </ul>

          <div className={css.recaptchaHolder}>
            <Reaptcha sitekey={REACT_APP_RECAPTCHA_SITE_KEY} onVerify={onVerify} />
          </div>

          <div className={css.bottomWrapper}>
            <Button type="submit" inProgress={submitInProgress} disabled={submitDisabled} className={css.contactUsButton}>
              <FormattedMessage id="JobsForm.button" />
            </Button>
          </div>
          {submitApplicationSuccess &&  (
            <p className={css.successMsg}>
              <FormattedMessage id="ContactForm.successMsg" values={{ lineBreak: <br/>}} />
            </p>
          )}
          {!!submitApplicationError &&  (
            <p className={css.error}>
              <FormattedMessage id="EnquiryForm.sendEnquiryError" />
            </p>
          )}

        </Form>
      );
    }}
  />
);

JobsFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  form: null,
  inProgress: false,
};

const { string, bool } = PropTypes;

JobsFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  form: string,
  inProgress: bool,

};

const JobsForm = compose(injectIntl)(JobsFormComponent);
JobsForm.displayName = 'JobsForm';

export default JobsForm;
